/**
 * GoogleMapsImportCard — Vendor / Venue dashboard widget to pull public
 * photos from a Google Maps business listing straight into the BeeWeddy
 * portfolio gallery (no manual download / re-upload).
 *
 * Backend: /api/google-maps/import-photos
 * Eligibility: role === 'vendor' (includes the 'venue' category). Pros and
 * Entertainment Pros do NOT see this card.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, CloudArrowDown, Spinner, Info,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleMapsImportCard = () => {
  const [status, setStatus] = useState(null);
  const [url, setUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/google-maps/status`,
          { withCredentials: true },
        );
        setStatus(data);
      } catch {
        setStatus({ configured: false, eligible: false });
      }
    })();
  }, []);

  const handleImport = async () => {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      toast.error('Paste your Google Maps Place URL first');
      return;
    }
    setImporting(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/google-maps/import-photos`,
        { maps_url: trimmed },
        { withCredentials: true },
      );
      setLastResult(data);
      if (data.skipped_cap) {
        toast.warning(data.message || 'Photo cap already reached');
      } else if (data.imported === 0) {
        toast.info(data.message || 'No new photos found on that Place');
      } else {
        toast.success(`Imported ${data.imported} photo${data.imported === 1 ? '' : 's'} from Google Maps`);
        setUrl('');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Maps import failed');
    } finally {
      setImporting(false);
    }
  };

  // Hide card entirely if backend says user is ineligible (e.g. pros)
  if (!status || !status.eligible) return null;

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="google-maps-import-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <MapPin size={20} className="text-red-600" weight="fill" /> Google Maps Photos
        </CardTitle>
        <CardDescription>
          Paste your Google Maps business listing URL and we&apos;ll import up to 10 of
          your public photos straight into your BeeWeddy portfolio gallery.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!status.configured ? (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200" data-testid="google-maps-unconfigured">
            <Info size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              Google Maps integration is not configured on the server yet. Admin
              needs to add the Places API key in <code>backend/.env</code>.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="maps-url-input" className="text-xs uppercase tracking-wider font-bold text-text-secondary">
                Google Maps Place URL
              </Label>
              <Input
                id="maps-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..."
                className="rounded-none"
                data-testid="google-maps-url-input"
              />
              <p className="text-[11px] text-text-secondary">
                Open your business on Google Maps → tap <strong>Share</strong> → copy the link.
              </p>
            </div>

            <Button
              onClick={handleImport}
              disabled={importing || !url.trim()}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              data-testid="google-maps-import-btn"
            >
              {importing
                ? <><Spinner size={14} className="mr-2 animate-spin" /> Importing…</>
                : <><CloudArrowDown size={14} className="mr-2" /> Import Photos from Maps</>}
            </Button>

            {lastResult && (
              <div className="text-xs text-text-secondary pt-2 border-t border-[#E8E1D5]" data-testid="google-maps-result">
                Last import: <strong>{lastResult.imported}</strong> photo{lastResult.imported === 1 ? '' : 's'} from {lastResult.candidates_seen} candidates.
                {lastResult.remaining_capacity !== undefined && (
                  <span className="ml-1 italic">Capacity left: {lastResult.remaining_capacity}.</span>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GoogleMapsImportCard;
